import { type ExpandedState, getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import React, { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { BpDesignSystem } from '../../types';
import { isFunction, mergeClassNames, useDebounce, useEffectOnce } from '../../utils';
import { useDeepMemo } from '../../utils/useDeepMemo';
import { Fade } from '../Fade';
import { DataTableProps, DataTableTypes } from './DataTableTypes';
import { dataTableClassnames } from './impl/const/classNames';
import { DataTableContextProvider } from './impl/context/DataTableContext';
import { toDataTableColumns } from './impl/mappers/toDataTableColumns';
import { toInitialColumnConfigurationState } from './impl/mappers/toInitialColumnConfigurationState';

import { toRowId } from './impl/mappers/toRowId';
import { toRowSelectionState } from './impl/mappers/toRowSelectionState';
import { toTableModel, toTableModelWithDefaults } from './impl/mappers/toTableModel';
import { Footer } from './impl/ui/Footer';
import { Header } from './impl/ui/Header';
import { Table } from './impl/ui/Table';
import { BulkToolbar } from './impl/ui/toolbar/BulkToolbar';
import { Toolbar } from './impl/ui/toolbar/Toolbar';
import { Wrapper } from './impl/ui/Wrapper';
import { getDerivedState } from './impl/utils/getDerivedState';
import { useColumnConfigurationStore } from './impl/utils/useColumnConfiguration';

export type DataTableAPI =
  | {
      unselectAll(): void;
    }
  | undefined;

const DataTableCore = <
  DATA_MODEL extends DataTableTypes.DataModel,
  QUERY_MODEL extends DataTableTypes.QueryModel = DataTableTypes.QueryModel,
  SORT_MODEL extends DataTableTypes.SortModel = DataTableTypes.SortModel
>(
  props: BpDesignSystem.PropsWithChildren<DataTableProps<DATA_MODEL, QUERY_MODEL, SORT_MODEL>>,
  ref: ForwardedRef<DataTableAPI | undefined>
) => {
  const derivedState = getDerivedState(props);
  const { pageModel, features } = derivedState;
  const [selectedRows, setSelectedRows] = useState<DataTableTypes.SelectedRow<DATA_MODEL>[]>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const hasSelectedRows = selectedRows.length > 0;
  const model = toTableModel<QUERY_MODEL, SORT_MODEL>(props.model);

  const { columnConfiguration, setColumnConfiguration } = useColumnConfigurationStore(
    props.initialColumnConfiguration,
    props.onColumnConfigurationChange
  );

  useImperativeHandle<DataTableAPI, DataTableAPI>(ref, () => ({
    unselectAll: () => {
      setSelectedRows([]);
    }
  }));

  useEffectOnce(() => {
    if (props.requestOnInit) {
      props.onModelChange?.(model);
    }
  });

  const handleModelChange = useCallback(
    (nextModel: DeepPartial<DataTableTypes.TableModel<QUERY_MODEL, SORT_MODEL>>) => {
      props.onModelChange?.(
        toTableModelWithDefaults(
          model as DataTableTypes.TableModel<any, any>,
          nextModel as DataTableTypes.TableModel<any, any>
        )
      );
    },
    [props.onModelChange, model]
  );

  const debouncedHandleModelChange = useDebounce(handleModelChange, props?.debounceModelChange ?? 300);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mapping should happen also for selectedRows, expanded changes
  const columns = useDeepMemo(
    () => toDataTableColumns<DATA_MODEL>(props.columns, derivedState),
    [props.columns, derivedState, selectedRows, expanded]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: collapse if data changes
  useEffect(() => {
    setExpanded({});
  }, [props.data?.length]);

  const tableInstance = useReactTable<DATA_MODEL>({
    columns,
    data: props.data,
    defaultColumn: {
      maxSize: 30, // minWidth is only used as a limit for resizing
      size: 150, // width is used for both the flex-basis and flex-grow
      minSize: 200
    },
    // initialState: initialStateToReactTableModel(initialState, hasColumnResize),
    manualPagination: true,
    rowCount: pageModel.totalCount,
    enableColumnResizing: features.columnManagement.resizable.enabled,
    columnResizeMode: features.columnManagement.resizable.mode,
    columnResizeDirection: features.columnManagement.resizable.direction,
    enableRowSelection: features.selectable.enabled,
    getRowId: !!features.selectable.key ? (row) => toRowId(row, features.selectable.key) : undefined,
    autoResetPageIndex: false,
    autoResetExpanded: false,
    enableSorting: false,
    manualSorting: true,
    manualExpanding: !!props.slotProps?.expandableRow?.render,
    getSubRows: features.expandable.enabled
      ? (row) => {
          if (!features.expandable.key) {
            return undefined;
          }
          if (typeof features.expandable.key === 'string') {
            return (row as Record<string, any>)[features.expandable.key];
          }
          return features.expandable.key(row);
        }
      : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onColumnSizingChange: (updater) => {
      const nextColumnSizing = isFunction(updater) ? updater(columnConfiguration.columnSizing ?? {}) : updater;
      setColumnConfiguration(
        {
          columnSizing: nextColumnSizing
        },
        true
      );
    },
    onRowSelectionChange: (updater) => {
      const nextSelectedRows = isFunction(updater) ? updater(toRowSelectionState(selectedRows)) : updater;
      setSelectedRows((prevState) => {
        return Object.keys(nextSelectedRows)
          .map((key) => {
            /*
             * Take fresh data first and then from previous selected rows
             */
            const data =
              props.data.find((row) => toRowId(row, features.selectable.key) === key) ??
              prevState.find((it) => it.id === key)?.data ??
              null;

            if (!data) {
              return null;
            }
            return {
              id: key,
              data
            };
          })
          .filter((it) => it !== null);
      });
    },
    onPaginationChange: (updater) => {
      setExpanded({});
      const nextState = isFunction(updater)
        ? updater({
            pageIndex: pageModel.currentPage,
            pageSize: pageModel.limit
          })
        : updater;

      handleModelChange({
        sort: model.sort,
        query: model.query,
        page: {
          limit: pageModel.limit,
          offset: nextState.pageIndex * pageModel.limit
        }
      } as unknown as DeepPartial<DataTableTypes.TableModel<QUERY_MODEL, SORT_MODEL>>);
    },
    onExpandedChange: setExpanded,
    initialState: toInitialColumnConfigurationState(
      props.initialColumnConfiguration,
      derivedState.features.columnManagement.resizable.enabled
    ),
    state: {
      rowSelection: toRowSelectionState(selectedRows),
      expanded,
      columnSizing: columnConfiguration.columnSizing,
      pagination: {
        pageIndex: pageModel.currentPage,
        pageSize: pageModel.limit
      }
    },
    debugTable: false,
    debugHeaders: false,
    debugColumns: false
  });

  return (
    <DataTableContextProvider
      tableInstance={tableInstance}
      derivedState={derivedState}
      selectedRows={selectedRows}
      slots={props.slots}
      slotProps={props.slotProps}
      model={model}
      setModelChange={debouncedHandleModelChange}
      setColumnConfigurationChange={setColumnConfiguration}
    >
      <Wrapper
        dataTest={props.dataTest ?? dataTableClassnames.root}
        style={props.style}
        className={mergeClassNames(`${dataTableClassnames.root}${props.className ? ` ${props.className}` : ''}`, {
          [dataTableClassnames.variant(derivedState.ui.variant)]: true,
          'has-pagination': tableInstance.getPageCount() > 1
        })}
        sx={props.sx}
        slots={{
          top: (
            <Header>
              {props.slots?.header}
              {derivedState.ui.show.toolbar && <Toolbar />}
              <Fade in={hasSelectedRows} unmountOnExit timeout={100}>
                <span>{props.slots?.bulkToolbar ?? <BulkToolbar />}</span>
              </Fade>
            </Header>
          ),
          bottom: tableInstance.getPageCount() > 1 ? <Footer /> : undefined
        }}
        role="presentation"
      >
        <Table />
      </Wrapper>
      {props.children}
    </DataTableContextProvider>
  );
};

export const DataTable = forwardRef(DataTableCore) as <
  DATA_MODEL extends DataTableTypes.DataModel,
  QUERY_MODEL extends DataTableTypes.QueryModel = DataTableTypes.QueryModel,
  SORT_MODEL extends DataTableTypes.SortModel = DataTableTypes.SortModel
>(
  props: BpDesignSystem.PropsWithChildren<DataTableProps<DATA_MODEL, QUERY_MODEL, SORT_MODEL>> & {
    ref?: React.ForwardedRef<DataTableAPI | undefined>;
  }
) => React.ReactNode;
